import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationModal from '../components/NotificationModal/NotificationModal';
import { setLastReceivedTestId } from '../components/SidebarOptions/TestAlarm/testAlarmSlice';
import { logoutUser, tryDeletePushToken } from '../containers/Sidebar/actionCreators';
import { useAppDispatch } from '../hooks';
import { store } from '../store';
import { AppOpenedNotificationHandler, DataOnlyHandler } from './notificationHandlers';
import { PushyService } from './PushyService';
import {
  pruneTenantSession,
  selectActiveTenantId,
  selectTenantSessionById,
  setSessionAlarm,
} from '../containers/Organizations/OrganizationsSlice';
import { resetTenantScopedSlices } from '../containers/Organizations/OrganizationsSlice/resetTenantScoped';
import {
  MAX_SIGN_OUT_FALLBACK_ATTEMPTS,
  trySwitchFirstRemainingTenantSession,
} from '../containers/Organizations/OrganizationsSlice/trySwitchFirstRemainingTenantSession';
import { isLwtSenderFeaturesEnabled, isMultiOrgFeatureEnabled } from '../utils/features';
import { switchActiveOrganization } from '../containers/Organizations/OrganizationsSlice/switchActiveOrganization';
import { refreshSessionAlarm } from '../containers/Organizations/OrganizationsSlice/syncSessionIndicators';
import type {
  AppNotificationParams,
  Notification,
  NotificationPayload,
} from '../shared/notifications';
import {
  EAlertType,
  EIdTypes,
  ENotificationDataProperties,
  ENotificationType,
  SendMessageType,
} from '../shared/notifications';
import { showAppToast } from '../components/AppToast/showAppToast';
import { getLwtAlarmData } from '../containers/LoneWorkerTimer/loneWorkerTimerSlice';
import {
  fetchAllActiveAlarms,
  getEmergencyTypesAction,
} from '../containers/Alarm/AlarmSlice/actionCreators';
import i18n from '../i18n';
import { fetchAlarmData } from '../containers/LoneWorkerTimer/loneWorkerTimerSlice/actionCreator';

// Leading edge keeps the silent-mode banner immediate on the first push;
// trailing edge coalesces the burst a multi-group alarm produces and
// reconciles with the final state. showLoading is off so the background
// refresh doesn't flip the alarm-creation screens into their loader state.
const debouncedRefreshActiveAlarms = _.debounce(
  () => {
    store.dispatch(fetchAllActiveAlarms());
    store.dispatch(getEmergencyTypesAction(false));
  },
  2000,
  { leading: true, trailing: true }
);

export const PushNotifications = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notification, setNotification] = useState<Notification>({
    messageId: null,
    title: '',
    description: '',
    alert: EAlertType.none,
  });
  const [notificationData, setNotificationData] = useState<
    NotificationPayload | undefined | null
  >();
  const [showModal, setShowModal] = useState(true);
  const [notificationType, setNotificationType] = useState(ENotificationType.message);

  let messageId: number | null = null;

  const navigateToResource = async ({
    type,
    id,
    tenantId,
  }: {
    type?: EAlertType;
    id?: number | null;
    tenantId?: number;
  }) => {
    const activeTenantId = selectActiveTenantId(store.getState());
    const notificationTenant = tenantId
      ? selectTenantSessionById(tenantId)(store.getState())
      : undefined;
    if (
      isMultiOrgFeatureEnabled() &&
      notificationTenant &&
      activeTenantId &&
      tenantId &&
      tenantId !== activeTenantId
    ) {
      if (getLwtAlarmData(store.getState())) {
        showAppToast({
          variant: 'error',
          message: i18n.t('tenant_session_switch_error_lwt_active'),
        });
        return;
      }
      const switchResult = await dispatch(switchActiveOrganization(tenantId));
      if (!switchResult.ok) {
        if (!switchResult.hasAlertedUser) {
          showAppToast({ variant: 'error', message: i18n.t('error_try_again') });
        }
        return;
      }
    }

    if (
      type === EAlertType.reactivate ||
      type === EAlertType.started ||
      type === EAlertType.shared ||
      type === EAlertType.ended
    ) {
      navigate(id ? `checklist/${id}` : 'checklists');
    } else if (type === EAlertType.callAlert || type === EAlertType.callAlertEnded) {
      navigate(id ? `oncall/${id}` : 'onCallAlert');
    } else if (type === EAlertType.newMuster || type === EAlertType.muster) {
      navigate(id ? `muster/${id}` : `imOk`);
    } else {
      navigate(id ? `message/${id}` : 'chat');
    }
  };

  useEffect(() => {
    if (notification.alert !== EAlertType.none && !showModal) {
      navigateToResource({
        type: notification.alert,
        id: notification.messageId,
        tenantId: notification.tenantId,
      });
    }
  }, [notification]);

  const formatId = (obj: any) => {
    const idTypeList = Object.keys(EIdTypes);
    const newObj = { ...obj };
    _.forEach(Object.keys(newObj), (key: string) => {
      if (idTypeList.includes(key)) {
        newObj.id = newObj[key];
        delete newObj[key];
      }
      if (newObj?.id) messageId = newObj.id;
    });
    return newObj;
  };

  const parseData = (data: any): any => {
    messageId = null; //reset messageId before each function call
    if (!data) {
      return data;
    }

    const propertyList = Object.keys(ENotificationDataProperties);
    _.forEach(propertyList, (key: string) => {
      if (propertyList.includes(key) && typeof data[key] === 'string') {
        data[key] = formatId(JSON.parse(data[key]));
      }
      if (key === EIdTypes.messageId && data[key]) {
        messageId = data[key];
      }
    });
    return data;
  };

  const defineNotificationType = (data: NotificationPayload) => {
    if (data.onCallAlert || data.onCallAlertEnded) {
      return ENotificationType.onCallAlert;
    } else if (
      data.checklistStarted ||
      data.checklistReactivated ||
      data.checklistEnded ||
      data.checklistShared
    ) {
      return ENotificationType.checklist;
    } else if (data.newMuster || data.muster) {
      return ENotificationType.IamOk;
    } else {
      return ENotificationType.message;
    }
  };

  const onNotification = async ({
    notificationPayload,
    showNotification,
  }: AppNotificationParams) => {
    if (!notificationData) setNotificationData(notificationPayload);

    setShowModal(showNotification);
    const data = parseData(notificationPayload);
    setNotificationType(defineNotificationType(data));
    const isDataOnlyMode = data.dataOnlyNotification !== 'False';
    const activeRoute = window.location.hash;
    const dataOnlyHandler = new DataOnlyHandler(messageId, activeRoute, dispatch);
    const entityChangedEvent = data?.entityChangedEvent;
    const isMessageReactionModified = entityChangedEvent?.entityType === 'MessageReaction';
    const isMessageReplyReactionModified =
      entityChangedEvent?.entityType === 'MessageReplyReaction';
    const isChecklistModified =
      entityChangedEvent?.entityType === 'Checklist' &&
      entityChangedEvent?.entityChangeType === 'Modified';

    const Handler = new AppOpenedNotificationHandler(
      messageId,
      data,
      setNotification,
      setIsModalOpen,
      showNotification
    );

    const activeSessionTenantId = selectActiveTenantId(store.getState());
    const notificationTenantId = notificationPayload?.tenantId
      ? Number(notificationPayload.tenantId)
      : undefined;
    const notificationTenantSession = notificationTenantId
      ? selectTenantSessionById(notificationTenantId)(store.getState())
      : undefined;
    const notificationTenantName = notificationTenantSession?.tenantName;
    const isInactiveSessionNotification =
      isMultiOrgFeatureEnabled() &&
      !!notificationTenantId &&
      !!activeSessionTenantId &&
      notificationTenantId !== activeSessionTenantId;
    const isActiveSessionNotification = !isInactiveSessionNotification;

    // Notification for a tenant that is no longer in our session registry
    // (pruned after expiry). Drop the visual notification — the session can't
    // be switched into, and showing a modal for an unknown org is confusing.
    if (isInactiveSessionNotification && !notificationTenantSession) {
      return;
    }

    if (data) {
      // Cross-org alarm indicator update. MUST run before any branch/early-return
      // below that targets *existing* inactive sessions — placing this after a gate
      // would let inactive-session indicators silently stop updating. A single recall
      // does not mean "no alarms": multiple can be active in a tenant concurrently,
      // so reconcile against the server's ActiveEmergency.
      // (The pruned-session early return above is exempt: no session row means
      // setSessionAlarm / reconcileSessionIndicators are no-ops anyway.)
      if (isInactiveSessionNotification && notificationTenantId) {
        if (data.recalled) {
          dispatch(refreshSessionAlarm(notificationTenantId));
        } else if (data.groupMessage?.type === SendMessageType.Alarm) {
          dispatch(setSessionAlarm({ tenantId: notificationTenantId, value: true }));
        }
      }

      // An alarm starting or ending changes which alarms block others. Refresh
      // the active-alarm list (and the emergency types blocking alarms are
      // rendered from) so silent-mode UI and the send-alarm screen react while
      // the user stays on the current screen. Runs for data-only notifications
      // too, which the modal branches below skip.
      if (
        isActiveSessionNotification &&
        (data.groupMessage?.type === SendMessageType.Alarm || data.recalled)
      ) {
        debouncedRefreshActiveAlarms();
      }

      if (isActiveSessionNotification) {
        dataOnlyHandler.handleMessages();
      }
      const entityId =
        entityChangedEvent?.entityIds?.length > 0 ? entityChangedEvent?.entityIds[0] : null;

      /**
       * For message replies:
       * 1) show pop up if the user is viewing a different message thread
       * 2) redirect the user to the correct message thread, if the app is closed and they tap on the push notification
       */
      if (data.reply && !isDataOnlyMode) {
        if (activeRoute.includes('message/') && !activeRoute.includes(`message/${messageId}`)) {
          Handler.openNotification({
            alert: EAlertType.reply,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        } else if (!showNotification) {
          await navigateToResource({
            type: EAlertType.reply,
            id: messageId,
            tenantId: notificationTenantId,
          });
        }
        return;
      }

      if (
        data.groupMessage &&
        !isDataOnlyMode &&
        [
          SendMessageType.RegularMessage,
          SendMessageType.HoldingStatement,
          SendMessageType.Broadcast,
        ].includes(data.groupMessage.type)
      ) {
        Handler.openNotification({
          alert: EAlertType.reply,
          tenantId: notificationTenantId,
          tenantName: notificationTenantName,
        });
      } else if (
        data.groupMessage &&
        !isDataOnlyMode &&
        [SendMessageType.GroupAdditionNormal, SendMessageType.Forwarded].includes(
          data.groupMessage.type
        )
      ) {
        // When a user is added to an existing message thread or receives a forwarded message,
        // no popup should be shown if the app is open. Navigate to the correct screen if
        // the user tapped the push notification while the app was closed.
        if (!showNotification) {
          await navigateToResource({
            type: EAlertType.reply,
            id: data.groupMessage.id,
            tenantId: notificationTenantId,
          });
        }
      } else if (
        ((data.groupMessage && data.groupMessage.type === SendMessageType.Alarm) ||
          data.recalled) &&
        !isDataOnlyMode
      ) {
        Handler.openNotification({
          alert: data.recalled ? EAlertType.recalled : EAlertType.active,
          showIcon: !data.recalled,
          cancelBtnTx: data.recalled ? '' : 'cancel',
          tenantId: notificationTenantId,
          tenantName: notificationTenantName,
        });
        if (data.recalled && isActiveSessionNotification) {
          dataOnlyHandler.handleRecalledAlert();
        }
      } else if (data.onCallAlert) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleOnCallAlert();
          }
        } else {
          Handler.openNotification({
            alert: EAlertType.callAlert,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        }
      } else if (data.onCallAlertEnded) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleOnCallAlert();
          }
        } else {
          Handler.openNotification({
            alert: EAlertType.callAlertEnded,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        }
      } else if (isChecklistModified) {
        if (isActiveSessionNotification) {
          dataOnlyHandler.handleChecklistModified(entityId);
        }
      } else if (data.checklistReactivated) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleCheckList();
          }
        } else {
          const checklistReactivatedId = data?.checklistReactivated?.id;
          const isChecklistReactivatedView = activeRoute?.includes(
            `checklist/${checklistReactivatedId}`
          );
          if (!isChecklistReactivatedView) {
            Handler.openNotification({
              alert: EAlertType.reactivate,
              tenantId: notificationTenantId,
              tenantName: notificationTenantName,
            });
          }
        }
      } else if (data.checklistEnded) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleCheckList();
          }
        } else {
          const checklistEndedId = data?.checklistEnded?.id;
          const isChecklistEndedView = activeRoute?.includes(`checklist/${checklistEndedId}`);
          if (!isChecklistEndedView) {
            Handler.openNotification({
              alert: EAlertType.ended,
              tenantId: notificationTenantId,
              tenantName: notificationTenantName,
            });
          }
        }
      } else if (data.checklistStarted) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleCheckList();
          }
        } else {
          Handler.openNotification({
            alert: EAlertType.started,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        }
      } else if (data.checklistShared) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleCheckList();
          }
        } else {
          Handler.openNotification({
            alert: EAlertType.shared,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        }
      } else if (data.newMuster) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleIamOk();
          }
        } else {
          Handler.openNotification({
            alert: EAlertType.newMuster,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        }
      } else if (data.muster) {
        if (isDataOnlyMode) {
          if (isActiveSessionNotification) {
            dataOnlyHandler.handleIamOk();
          }
        } else {
          Handler.openNotification({
            alert: EAlertType.muster,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        }
      } else if (data?.personalAlarmWarning && !isDataOnlyMode) {
        Handler.openNotification({
          alert: EAlertType.alarmWarn,
          cancelBtnTx: 'close',
          tenantId: notificationTenantId,
          tenantName: notificationTenantName,
        });
      } else if (data?.data?.LoggedOut) {
        if (!isMultiOrgFeatureEnabled()) {
          await tryDeletePushToken();
          dispatch(logoutUser());
        } else if (
          notificationTenantId &&
          activeSessionTenantId &&
          activeSessionTenantId !== notificationTenantId
        ) {
          dispatch(pruneTenantSession({ tenantId: notificationTenantId, alertUser: true }));
        } else if (activeSessionTenantId) {
          dispatch(pruneTenantSession({ tenantId: activeSessionTenantId, alertUser: true }));
          dispatch(resetTenantScopedSlices());
          const hasSwitchedSession = await dispatch(
            trySwitchFirstRemainingTenantSession({
              maxAttempts: MAX_SIGN_OUT_FALLBACK_ATTEMPTS,
            })
          );
          if (hasSwitchedSession) {
            // Force-logout of the active session swapped to a fallback tenant —
            // redirect to the main screen so the new user sees fresh data (#2349).
            navigate('/dashboard');
          } else {
            await tryDeletePushToken();
            dispatch(logoutUser());
          }
        }
      } else if (data?.data?.SelfAlarmTest && !isDataOnlyMode) {
        if (showNotification) {
          Handler.openNotification({
            alert: EAlertType.selfAlarmOpenApp,
            cancelBtnTx: 'close',
            closeBtnOnly: true,
            tenantId: notificationTenantId,
            tenantName: notificationTenantName,
          });
        } else if (isActiveSessionNotification) {
          const currentTestId = store.getState().testAlarm.testId;
          dispatch(setLastReceivedTestId(currentTestId));
          navigate('/sidebar/test-alarm');
        }
      } else if (isMessageReactionModified) {
        if (isActiveSessionNotification) {
          dataOnlyHandler.handleMessageReactionsModified(entityId);
        }
      } else if (isMessageReplyReactionModified) {
        if (isActiveSessionNotification) {
          dataOnlyHandler.handleMessageReplyReactionsModified(entityId);
        }
      } else if (process.env.REACT_APP_E2E && data?.personalAlarmOneMinuteWarning) {
        Handler.openNotification({
          alert: EAlertType.reply,
          cancelBtnTx: 'close',
          closeBtnOnly: true,
          tenantId: notificationTenantId,
          tenantName: notificationTenantName,
        });
      } else if (isLwtSenderFeaturesEnabled() && data?.personalAlarmDispatched) {
        dispatch(fetchAlarmData(navigate, true));
      }
    }
  };

  const titleColor = notification.alert === EAlertType.recalled ? 'green' : 'white';

  const closeModal = () => {
    setIsModalOpen(false);
    setNotification({
      messageId: null,
      title: '',
      description: '',
      alert: EAlertType.none,
    });
  };

  const cancelModal = () => {
    closeModal();
    if (notification.alert === EAlertType.selfAlarmOpenApp) {
      onNotification({ notificationPayload: notificationData!, showNotification: false });
    }
    const dataHandler = new DataOnlyHandler(messageId, window.location.hash, dispatch);
    if (notificationType === ENotificationType.onCallAlert) {
      dataHandler.handleOnCallAlert();
    } else if (notificationType === ENotificationType.checklist) {
      dataHandler.handleCheckList();
    } else if (notificationType === ENotificationType.IamOk) {
      dataHandler.handleIamOk();
    }
  };

  const confirmModal = async () => {
    await navigateToResource({
      type: notification.alert,
      id: notification.messageId,
      tenantId: notification.tenantId,
    });
    setIsModalOpen(false);
  };

  return (
    <>
      <NotificationModal
        isOpen={isModalOpen}
        onSubmit={confirmModal}
        onCancel={cancelModal}
        title={notification.title}
        description={notification.description}
        tenantName={notification.tenantName}
        confirmText={notification.confirmBtnTx || 'open'}
        cancelText={notification.cancelBtnTx || 'btnLater'}
        titleColor={titleColor}
        showIcon={notification?.showIcon}
        showModal={showModal}
        closeBtnOnly={notification.closeBtnOnly}
      />
      <PushyService onNotification={onNotification} />
    </>
  );
};
